import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

/** Standard page wrapper: centred column with PlayAd spacing. */
export function Page({ children, className, wide, full }: { children: ReactNode; className?: string; wide?: boolean; full?: boolean }) {
  return (
    <div className={cn("mx-auto w-full px-4 pb-16 pt-6 sm:px-8 sm:pt-8", full ? "max-w-none" : wide ? "max-w-[1440px]" : "max-w-[1180px]", className)}>{children}</div>
  );
}

export function PageHeader({
  title,
  count,
  subtitle,
  actions,
  info,
  className,
  below,
}: {
  title: ReactNode;
  count?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  info?: ReactNode;
  className?: string;
  below?: ReactNode;
}) {
  return (
    <header className={cn("mb-6", className)}>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2.5 text-[26px] font-semibold leading-tight tracking-[-0.02em]">
            <span className="truncate">{title}</span>
            {info}
            {count !== undefined && count !== null && <span className="text-[13px] font-normal tracking-normal text-muted tnum">{count}</span>}
          </h1>
          {subtitle && <p className="mt-1 text-[13px] text-muted">{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {below && <div className="mt-5">{below}</div>}
    </header>
  );
}

/** Coloured robot-face mascot (agent chips, Ask button, thinking row). */
export function RobotFace({ color = "#e08a3c", className, eyes = "#111" }: { color?: string; className?: string; eyes?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={cn("size-5", className)} aria-hidden>
      <rect x="2.5" y="5" width="19" height="15" rx="5" fill={color} />
      <rect x="10.8" y="2" width="2.4" height="3.5" rx="1.2" fill={color} />
      <rect x="6.3" y="10" width="3.6" height="4.6" rx="1.8" fill={eyes} />
      <rect x="14.1" y="10" width="3.6" height="4.6" rx="1.8" fill={eyes} />
    </svg>
  );
}

/** Thin progress bar. */
export function Progress({ value, className, tone = "primary" }: { value: number; className?: string; tone?: "primary" | "success" | "danger" | "warning" }) {
  const v = Math.max(0, Math.min(100, value));
  const color = tone === "success" ? "bg-success" : tone === "danger" ? "bg-danger" : tone === "warning" ? "bg-warning" : "bg-primary";
  return (
    <div className={cn("h-1.5 w-full overflow-hidden rounded-full bg-elevated", className)} role="progressbar" aria-valuenow={v} aria-valuemin={0} aria-valuemax={100}>
      <div className={cn("h-full rounded-full transition-[width] duration-500", color)} style={{ width: `${v}%` }} />
    </div>
  );
}

/** Circular context/usage ring. */
export function Ring({ value, size = 16, stroke = 2.5, className }: { value: number; size?: number; stroke?: number; className?: string }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(100, value));
  return (
    <svg width={size} height={size} className={className} aria-hidden>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#3a3a3e" strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={v > 85 ? "var(--danger)" : v > 60 ? "var(--warning)" : "var(--link)"}
        strokeWidth={stroke}
        strokeDasharray={c}
        strokeDashoffset={c - (v / 100) * c}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}
